import * as React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Skeleton } from './skeleton';

export interface DataTableColumn<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => unknown);
  render?: (value: unknown, row: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

type SortDirection = 'asc' | 'desc';

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  loadingRows?: number;
  emptyMessage?: string;
  onRowClick?: (row: T, index: number) => void;
  rowKey?: keyof T | ((row: T, index: number) => string | number);
  className?: string;
  defaultSort?: SortState;
  /** Controlled sort state (server-side). When provided, client-side sort is skipped. */
  sort?: SortState | null;
  /** Called when user clicks a sortable column header in controlled mode. */
  onSortChange?: (sort: SortState | null) => void;
}

function getAccessorValue<T>(row: T, accessor: DataTableColumn<T>['accessor']): unknown {
  if (typeof accessor === 'function') {
    return accessor(row);
  }
  return row[accessor];
}

function getRowKeyValue<T>(
  row: T,
  index: number,
  rowKey?: DataTableProps<T>['rowKey']
): string | number {
  if (rowKey === undefined) {
    return index;
  }
  if (typeof rowKey === 'function') {
    return rowKey(row, index);
  }
  return String(row[rowKey]);
}

function sortData<T>(
  data: T[],
  sort: SortState | null,
  columns: DataTableColumn<T>[]
): T[] {
  if (!sort) {
    return data;
  }

  const column = columns.find((col) => col.id === sort.columnId);
  if (!column) {
    return data;
  }

  return [...data].sort((a, b) => {
    const aVal = getAccessorValue(a, column.accessor);
    const bVal = getAccessorValue(b, column.accessor);

    // Handle null/undefined
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sort.direction === 'asc' ? -1 : 1;
    if (bVal == null) return sort.direction === 'asc' ? 1 : -1;

    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const result = aVal.localeCompare(bVal);
      return sort.direction === 'asc' ? result : -result;
    }

    // Number comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    // Date comparison
    if (aVal instanceof Date && bVal instanceof Date) {
      return sort.direction === 'asc'
        ? aVal.getTime() - bVal.getTime()
        : bVal.getTime() - aVal.getTime();
    }

    // Fallback string comparison
    const aStr = String(aVal);
    const bStr = String(bVal);
    const result = aStr.localeCompare(bStr);
    return sort.direction === 'asc' ? result : -result;
  });
}

function DataTableInner<T>(
  {
    columns,
    data,
    loading = false,
    loadingRows = 5,
    emptyMessage = 'No data available',
    onRowClick,
    rowKey,
    className,
    defaultSort,
    sort: controlledSort,
    onSortChange,
  }: DataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const isControlled = onSortChange !== undefined;
  const [internalSort, setInternalSort] = React.useState<SortState | null>(defaultSort ?? null);

  // Active sort: controlled takes priority
  const activeSort = isControlled ? (controlledSort ?? null) : internalSort;

  const handleSort = React.useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.id === columnId);
      if (!column?.sortable) return;

      if (isControlled) {
        const next: SortState | null =
          activeSort?.columnId === columnId
            ? activeSort.direction === 'asc'
              ? { columnId, direction: 'desc' }
              : null
            : { columnId, direction: 'asc' };
        onSortChange!(next);
      } else {
        setInternalSort((prev) => {
          if (prev?.columnId !== columnId) {
            return { columnId, direction: 'asc' };
          }
          if (prev.direction === 'asc') {
            return { columnId, direction: 'desc' };
          }
          return null;
        });
      }
    },
    [columns, isControlled, activeSort, onSortChange]
  );

  // Skip client-side sort when controlled (server already sorted)
  const sortedData = React.useMemo(
    () => (isControlled ? data : sortData(data, internalSort, columns)),
    [data, internalSort, columns, isControlled]
  );

  const renderSortIcon = (column: DataTableColumn<T>) => {
    if (!column.sortable) return null;

    if (activeSort?.columnId !== column.id) {
      return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 text-muted-foreground/50" />;
    }
    if (activeSort.direction === 'asc') {
      return <ArrowUp className="ml-1 inline h-3.5 w-3.5" />;
    }
    return <ArrowDown className="ml-1 inline h-3.5 w-3.5" />;
  };

  return (
    <div ref={ref} className={cn('w-full overflow-auto', className)}>
      <table className="w-full caption-bottom text-sm">
        <thead className="[&_tr]:border-b">
          <tr className="border-b transition-colors hover:bg-muted/50">
            {columns.map((column) => (
              <th
                key={column.id}
                className={cn(
                  'h-12 px-4 text-left align-middle font-medium text-muted-foreground',
                  column.sortable && 'cursor-pointer select-none',
                  column.headerClassName
                )}
                onClick={column.sortable ? () => handleSort(column.id) : undefined}
              >
                <span className="inline-flex items-center">
                  {column.header}
                  {renderSortIcon(column)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {loading ? (
            Array.from({ length: loadingRows }, (_, rowIndex) => (
              <tr key={rowIndex} className="border-b transition-colors">
                {columns.map((column) => (
                  <td key={column.id} className="p-4 align-middle">
                    <Skeleton variant="text" />
                  </td>
                ))}
              </tr>
            ))
          ) : sortedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, index) => (
              <tr
                key={getRowKeyValue(row, index, rowKey)}
                className={cn(
                  'border-b transition-colors hover:bg-muted/50',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
              >
                {columns.map((column) => {
                  const value = getAccessorValue(row, column.accessor);
                  return (
                    <td
                      key={column.id}
                      className={cn('p-4 align-middle', column.className)}
                    >
                      {column.render ? column.render(value, row) : String(value ?? '')}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Use a wrapper to preserve generic type parameter with forwardRef
const DataTable = React.forwardRef(DataTableInner) as <T>(
  props: DataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export { DataTable };
